import { Controller } from '@hotwired/stimulus';
import { Helpers } from '../helpers';
import * as bootstrap from 'bootstrap';
import i18n from '../init/i18n';

export default class InvestFormController extends Controller {
  static targets = [
    'subscriptionAmount',
    'feesAmount',
    'fieldsErrors',
    'subscriptionAmountDivs',
    'feesAmountDivs',
    'totalAmountDivs',
    'nonInformedFields',
    'modalTotalWithFees',
    'submitButton',
    'insufficientBalance',
    'subscribeModal',
    'sddMandateFieldsContainer',
    'sddMandateField',
    'sddMandateCheckBoxInput',
    'sddMandateCheckBoxContainer',
    'subscribeConfirmation',
    'userRecommendationField'
  ];

  static values = {
    feesUrl: String,
    projectId: Number,
    projectName: String,
    profileBalance: Number,
    maxInvestRecommendation: Number,
    userMayHaveWarning: Boolean,
    sddMandateEnabled: Boolean,
  };

  connect() {
    this.sddMandateEnabled = this.sddMandateEnabledValue;
    this.toggleSubmit();
  }

  // Compute fees and total through backend
  computeFeesAndTotal() {
    const amount = this.subscriptionAmountTarget.value;
    let controller = this;

    Rails.ajax({
      type: 'GET',
      url: this.feesUrlValue,
      data: 'amount=' + amount + '&project_id=' + this.projectIdValue,
      dataType: 'json',
      success: function (response) {
        const feesAmount = parseFloat(response['fees_amount']);
        const totalWithFeesAmount = parseFloat(response['total_with_fees']);

        // Check if the amount is valid
        controller.checkAmountValidity(amount, totalWithFeesAmount);

        // Display fees and total
        controller.feesAmountTarget.value = feesAmount;
        controller.subscriptionAmountDivsTargets.innerHTML =
          controller.updateAmounts(
            controller.subscriptionAmountDivsTargets,
            parseFloat(amount)
          );
        controller.feesAmountDivsTargets.innerHTML = controller.updateAmounts(
          controller.feesAmountDivsTargets,
          feesAmount
        );
        controller.totalAmountDivsTargets.innerHTML = controller.updateAmounts(
          controller.totalAmountDivsTargets,
          totalWithFeesAmount
        );
        controller.modalTotalWithFeesTarget.innerHTML =
          Helpers.numberToCurrency(totalWithFeesAmount, 2);
      },
      error: function () {
        controller.fieldsErrorsTarget.innerHTML =
          'Une erreur s\'est produite, veuillez réessayer.';
      },
    });
  }

  checkAmountValidity(amount, totalWithFeesAmount) {
    this.insufficientBalanceTarget.classList.add('d-none');

    const profileBalance = parseFloat(this.profileBalanceValue);

    switch (true) {
      case !this.subscriptionAmountTarget.checkValidity():
        this.fieldsErrorsTarget.innerHTML =
          this.subscriptionAmountTarget.validationMessage;
        this.insufficientBalanceTarget.classList.remove('d-none');
        return this.triggerError();
      case amount === '':
        this.fieldsErrorsTarget.innerHTML =
          'Un montant de souscription doit être renseigné.';
        return this.triggerError();
      case !this.sddMandateEnabled && totalWithFeesAmount > profileBalance:
        this.insufficientBalanceTarget.classList.remove('d-none');
        this.fieldsErrorsTarget.innerHTML = '';
        return this.triggerError();
      default:
        this.fieldsErrorsTarget.innerHTML = '';
        this.subscriptionAmountTarget.classList.remove('is-invalid');
        return true;
    }
  }

  updateAmounts(container, amount) {
    const finalAmount =
      amount === 0 || isNaN(amount)
        ? '0,00 €'
        : Helpers.numberToCurrency(amount, 2);

    container.forEach((element) => {
      element.innerHTML = finalAmount;
    });
  }

  triggerError() {
    this.subscriptionAmountTarget.classList.add('is-invalid');
    return false;
  }

  validateAndShowSubscriptionModal() {
    const amount = this.subscriptionAmountTarget.value;
    const maxInvestRecommendation = this.maxInvestRecommendationValue;
    const totalWithFeesAmount =
      parseFloat(amount) + parseFloat(this.feesAmountTarget.value);

    if (!this.checkAmountValidity(amount, totalWithFeesAmount)) return;

    if (this.userMayHaveWarningValue === true) {
      let toggled = totalWithFeesAmount <= maxInvestRecommendation
      if (this.hasUserRecommendationFieldTarget) { this.userRecommendationFieldTarget.required = !toggled  }

      this.nonInformedFieldsTargets.forEach((field) => {
        field.classList.toggle('d-none', toggled)
      });
    }

    this.showModal(this.subscribeModalTarget);
  }

  preventAutoSubmit(event) {
    event.preventDefault();
    this.validateAndShowSubscriptionModal();
  }

  showModal(modalTarget) {
    const modal = new bootstrap.Modal(modalTarget);
    modal.show();
  }

  sddFieldsCompleted() {
    return this.sddMandateFieldTargets.every(
      field => field.value?.trim() !== ''
    )
  }

  toggleSubmit() {
    if (!this.sddMandateEnabled) {
      this.submitButtonTarget.classList.remove('disabled')
      return
    }
    this.submitButtonTarget.classList.toggle('disabled', !this.sddFieldsCompleted())
  }

  toggleSddMandate(toggled) {
    if (this.sddMandateFieldsContainerTarget) {
      this.sddMandateFieldsContainerTarget.classList.toggle('d-none', toggled);

      this.sddMandateCheckBoxContainerTarget.classList.toggle('d-none', toggled);
      this.sddMandateCheckBoxInputTarget.required = !toggled;

      const confirmationHtml = toggled ? 'subscribe_confirmation_html' : 'sdd_confirmation_html'
      this.subscribeConfirmationTarget.innerHTML = i18n.t(
        confirmationHtml,
        {
          project_name: this.projectNameValue,
          total_with_fees_html: this.buildTotalWithFeesHtml()
        }
      )

      this.sddMandateFieldTargets.forEach(field => {
        field.required = !toggled
      })

      if (!toggled) {
        this.toggleSubmit()
      } else {
        this.submitButtonTarget.classList.remove('disabled')
      }
    }
  }

  buildTotalWithFeesHtml() {
    return `
      <span class='text-nowrap' data-invest-form-target='modalTotalWithFees'>
        ${Helpers.numberToCurrency(this.computeTotalWithFeesAmount(this.subscriptionAmountTarget.value), 2)}
      </span>
    `
  }

  computeTotalWithFeesAmount(amount) {
    return parseFloat(amount || 0) + parseFloat(this.feesAmountTarget.value);
  }

  validateInvestment() {
    const amount = this.subscriptionAmountTarget.value
    this.checkAmountValidity(amount, this.computeTotalWithFeesAmount(amount))
  }

  enableSddMandate(event) {
    this.sddMandateEnabled = true;
    this.validateInvestment();
    this.toggleSddMandate(false)
  }

  disableSddMandate(event) {
    this.sddMandateEnabled = false;
    this.validateInvestment();
    this.toggleSddMandate(true)
  }
}
